import React, { useState } from "react";
import { Tabs } from "antd";
import {
  BookOpenCheck,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  CirclePlay,
  ClipboardList,
  Clock3,
  ExternalLink,
  ListChecks,
  Smartphone,
  Stethoscope,
  Tag,
  UserRound,
  Users,
} from "lucide-react";

const VIDEO_SECTIONS = [
  {
    id: "add-care-team",
    title: "Add Care Team",
    shortTitle: "Care Team",
    Icon: Users,
    videos: [
      {
        title: "Single onboarding",
        duration: "1:08",
        url: "https://drive.google.com/file/d/1Nbz7neNYDKYeSsNOQOOcyTZAsVMMUOeP/preview",
        description: "Add one hospital specialist and send their mobile-app access details.",
        steps: [
          "Open Care Team and select Add Team Member.",
          "Choose Single Entry and enter the specialist's personal details.",
          "Select the role: Nurse, Physiotherapist, or Nursing Assistant.",
          "Enter the registered mobile number and email address.",
          "Save the member and confirm they appear in the Care Team list.",
        ],
      },
      {
        title: "Bulk onboarding",
        duration: "2:50",
        url: "https://drive.google.com/file/d/1DH6IWwoYOUU1Phfs0eB32DLbU0SpvOCu/preview",
        description: "Onboard several specialists together using the bulk-entry template.",
        steps: [
          "Open Add Team Member and choose Bulk Entry.",
          "Download the sample template without changing its headings.",
          "Add each specialist's role, title, name, date of birth, phone, and email.",
          "Upload the completed Excel or CSV file and review validation errors.",
          "Select Upload & Save to add all valid specialists.",
        ],
      },
    ],
  },
  {
    id: "add-patient",
    title: "Add Patient",
    shortTitle: "Patients",
    Icon: UserRound,
    videos: [
      {
        title: "Single onboarding",
        duration: "2:36",
        url: "https://drive.google.com/file/d/1ge9k7LbekOQ6JFyGHmGg0BLRFm7jP7td/preview",
        description: "Register one patient, add their address, and upload a discharge summary.",
        steps: [
          "Open Patients, select Add Patient, and remain on Single.",
          "Enter the patient details and the mobile number used for app login.",
          "Add the correct home address manually or with a Google Maps link.",
          "Upload the discharge summary as a PDF and continue.",
          "Prescribe a care plan or select Skip Care Plan to finish onboarding.",
        ],
      },
      {
        title: "Bulk onboarding",
        duration: "1:55",
        url: "https://drive.google.com/file/d/197UmYy0F7iMQrNgXVUvRVeraOXyVOngx/preview",
        description: "Register multiple patients at once using the patient upload template.",
        steps: [
          "Open Add Patient and choose Bulk Upload.",
          "Download the sample file and retain the original column headings.",
          "Enter one patient per row with MRN, identity, date of birth, and phone details.",
          "Upload the completed file and correct any validation errors.",
          "Select Upload & Save; patients must later add their address in the mobile app.",
        ],
      },
    ],
  },
  {
    id: "duty-flow-mobile",
    title: "Duty Flow - Mobile",
    shortTitle: "Duty Flow",
    Icon: Stethoscope,
    videos: [
      {
        title: "Daily payment booking",
        duration: "4:30",
        url: "https://drive.google.com/file/d/1l_k39OQetWmyhO6veGJhftM7Q60UFdt7/preview",
        description: "Start a duty, document care, collect that day's payment, and close the visit.",
        steps: [
          "Log in with the specialist's registered mobile number and OTP.",
          "Open the ongoing visit and start duty from the patient's location.",
          "Review At a Glance, then record each service in Add Report.",
          "Generate the daily-payment QR for the current duty amount.",
          "Confirm payment, then submit the report and end duty from the patient location.",
        ],
      },
      {
        title: "On-location QR payment",
        duration: "4:49",
        url: "https://drive.google.com/file/d/1aXRwzC7sFHGusPsZWpQZo6OSyCLHPUhE/preview",
        description: "Collect the complete upfront amount by UPI at the patient's location.",
        steps: [
          "Log in as the assigned specialist and locate Pay on Location.",
          "Verify the patient, date, duty time, and location on the payment card.",
          "Generate the UPI QR during the permitted payment window.",
          "Ask the patient or caretaker to scan and complete the full payment.",
          "Confirm payment, open the ongoing visit, and continue the duty flow.",
        ],
      },
      {
        title: "Online payment booking",
        duration: "6:32",
        url: "https://drive.google.com/file/d/1mIl0DwwNlzOo0jVZE8g9Hl8XOh0JtEky/preview",
        description: "Follow an upfront online booking from patient payment through duty completion.",
        steps: [
          "The patient logs in with their registered number and four-digit OTP.",
          "Open the payment request and verify the visit information.",
          "Review the payment summary and complete payment through Razorpay.",
          "The specialist logs in, reaches the patient location, and starts duty.",
          "Record all services, submit the report, and end duty at the scheduled time.",
        ],
      },
      {
        title: "SOS emergency alert",
        duration: "0:35",
        url: "https://drive.google.com/file/d/1JeZu7M3lMHKqGKQvDe4w0qS_cKQXDzqJ/preview",
        description: "Send an emergency alert with patient details and the specialist's live location.",
        steps: [
          "After starting duty, select SOS when an emergency arises.",
          "Cancel within five seconds if SOS was selected accidentally.",
          "Allow the countdown to finish when emergency assistance is required.",
          "The app sends an SMS to the registered emergency contacts.",
          "Select Done after the alert has been sent.",
        ],
      },
    ],
  },
  {
    id: "prescribe-booking",
    title: "Prescribe & Booking",
    shortTitle: "Booking",
    Icon: ClipboardList,
    videos: [
      {
        title: "One-time nursing",
        duration: "5:33",
        url: "https://drive.google.com/file/d/1-MpzrBbnFvGt8Wm565R0BeIP4fIlrxsn/preview",
        description: "Prescribe a short nursing service and complete its staff assignment.",
        steps: [
          "Open a patient, select Prescribe, and choose or upload a discharge summary.",
          "Choose Nursing Care, the staff role, and One Time Service.",
          "Select services configured in Services & Fees and submit the care plan.",
          "Choose the date and slot; same-day slots require the four-hour preparation window.",
          "Assign duty and standby staff, select the payment mode, and confirm.",
        ],
      },
      {
        title: "Physiotherapy",
        duration: "6:30",
        url: "https://drive.google.com/file/d/1Q_t7zLAbABMPhuf2MhvC3Iyj6rDfpXkW/preview",
        description: "Create a physiotherapy plan with its treatment schedule and team.",
        steps: [
          "Select the patient, discharge summary, and Physiotherapy category.",
          "Choose configured services, treatment duration, and session frequency.",
          "Select the start date and review the calculated session dates.",
          "Choose the session slot and assign the physiotherapist and standby staff.",
          "Select the payment mode, review the booking, and assign it.",
        ],
      },
      {
        title: "Regular nursing",
        duration: "5:05",
        url: "https://drive.google.com/file/d/1drxVP24uSmmzGR9WbCRWeWQBPe6VUJOU/preview",
        description: "Create a multi-day, shift-based nursing booking for an existing patient.",
        steps: [
          "Select the patient and the relevant discharge summary.",
          "Choose Nursing Care, the staff role, and Regular Duty.",
          "Select the configured shift and service, then enter the number of days.",
          "Choose a future start date because regular duties cannot start the same day.",
          "Assign duty and standby staff, select payment, and confirm the booking.",
        ],
      },
    ],
  },
  {
    id: "service-pricing",
    title: "Services & Fees",
    shortTitle: "Services & Fees",
    Icon: Tag,
    videos: [
      {
        title: "One-time services",
        duration: "1:29",
        url: "https://drive.google.com/file/d/1mOE21uiA2xsX7_BWa-K0-efo3GfJ0wDm/preview",
        description: "Configure fees for individual nursing procedures.",
        steps: [
          "Open Services & Fees and choose Nurse, then One-Time Service.",
          "Select the procedure and enter its fee.",
          "Optionally add a percentage or fixed discount and notes.",
          "Select the plus icon and review the item under Pending Changes.",
          "Select Save Changes to move it to Saved Pricing.",
        ],
      },
      {
        title: "Regular services",
        duration: "1:23",
        url: "https://drive.google.com/file/d/1nUM1ZmQQ_lNtWUAm6XCGyqgD4T4jJBMa/preview",
        description: "Configure pricing for shift-based nursing care.",
        steps: [
          "Choose Regular Service and enter a service name.",
          "Select Nurse or Nursing Assistant, then choose the shift.",
          "Select the nursing activities included in the shift.",
          "Enter the fee, optional discount, and notes.",
          "Add the item and select Save Changes.",
        ],
      },
      {
        title: "Physiotherapy services",
        duration: "0:54",
        url: "https://drive.google.com/file/d/1Lx01j93xaCukm4JG343XNA77UljViXhw/preview",
        description: "Add fees for physiotherapy assessment and treatment services.",
        steps: [
          "Open the Physiotherapist pricing section.",
          "Select or enter the physiotherapy service.",
          "Enter the fee and any optional discount or notes.",
          "Select the plus icon and review Pending Changes.",
          "Select Save Changes to publish the pricing.",
        ],
      },
      {
        title: "Update and delete",
        duration: "0:44",
        url: "https://drive.google.com/file/d/1aWr0GS4KRRHCTpu5FTFSD6K3i0KyrWyB/preview",
        description: "Maintain existing service pricing without losing the review step.",
        steps: [
          "Find the service under Saved Pricing.",
          "Use Edit to change its details, then confirm the update.",
          "Use Delete when the service should be removed.",
          "Review the Update or Remove status under Pending Changes.",
          "Select Save Changes to apply the final action.",
        ],
      },
    ],
  },
  {
    id: "rescheduling",
    title: "Rescheduling",
    shortTitle: "Rescheduling",
    Icon: CalendarClock,
    videos: [
      {
        title: "Date, time and specialist",
        duration: "1:59",
        url: "https://drive.google.com/file/d/1V5A91n4Uo9lYs_cBhlV4UFjVMFyQhAUg/preview",
        description: "Move an eligible future duty and select an available specialist.",
        steps: [
          "Open the eligible paid or Daily Payment visit.",
          "Edit the duty date and select an available future date.",
          "Edit the time and choose from the available future slots.",
          "Edit the specialist and select an available care team member.",
          "Review each change and confirm the updated duty details.",
        ],
      },
    ],
  },
];

const SECTION_ORDER = [
  "service-pricing",
  "add-care-team",
  "add-patient",
  "prescribe-booking",
  "rescheduling",
  "duty-flow-mobile",
];

const ORDERED_VIDEO_SECTIONS = SECTION_ORDER.map((sectionId) =>
  VIDEO_SECTIONS.find((section) => section.id === sectionId),
).filter(Boolean);

const flatLessons = ORDERED_VIDEO_SECTIONS.flatMap((section) =>
  section.videos.map((video, videoIndex) => ({
    sectionId: section.id,
    videoIndex,
    sectionTitle: section.title,
    video,
  })),
);

const HowItWorks = () => {
  const [activeSectionId, setActiveSectionId] = useState(
    ORDERED_VIDEO_SECTIONS[0].id,
  );
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);

  const activeSection =
    ORDERED_VIDEO_SECTIONS.find(
      (section) => section.id === activeSectionId,
    ) || ORDERED_VIDEO_SECTIONS[0];
  const activeVideo = activeSection.videos[activeVideoIndex];
  const globalLessonIndex = flatLessons.findIndex(
    (lesson) =>
      lesson.sectionId === activeSection.id &&
      lesson.videoIndex === activeVideoIndex,
  );

  const selectSection = (sectionId) => {
    setActiveSectionId(sectionId);
    setActiveVideoIndex(0);
  };

  const openLesson = (lesson) => {
    setActiveSectionId(lesson.sectionId);
    setActiveVideoIndex(lesson.videoIndex);
  };

  const moveLesson = (direction) => {
    const nextIndex = globalLessonIndex + direction;
    if (nextIndex >= 0 && nextIndex < flatLessons.length) {
      openLesson(flatLessons[nextIndex]);
    }
  };

  const tabItems = ORDERED_VIDEO_SECTIONS.map((section) => {
    const SectionIcon = section.Icon;
    return {
      key: section.id,
      label: (
        <span className="flex items-center gap-2 px-1">
          <SectionIcon size={16} />
          <span>{section.shortTitle}</span>
        </span>
      ),
    };
  });

  return (
    <div className="font-poppins bg-white rounded-md shadow-lg p-2 h-[86vh] m-2 overflow-hidden">
      <main className="h-full min-h-0 bg-white rounded-lg border border-[#9EA3FF] flex flex-col overflow-hidden">
        <header className="flex-shrink-0 px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-9 w-9 flex-shrink-0 rounded-lg bg-[#4951EC] text-white flex items-center justify-center">
              <BookOpenCheck size={19} />
            </span>
            <div className="min-w-0">
              <h1 className="text-[15px] font-semibold text-gray-900 leading-5">
                How it works?
              </h1>
              <p className="text-[11px] text-gray-500 truncate">
                Guided videos for the most important portal workflows
              </p>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-2 text-[11px] text-gray-500 whitespace-nowrap">
            <CirclePlay size={14} className="text-[#4951EC]" />
            <span>{flatLessons.length} guided videos</span>
          </div>
        </header>

        <div className="flex-shrink-0 px-4 border-b border-gray-100 how-it-works-tabs">
          <Tabs
            activeKey={activeSection.id}
            items={tabItems}
            onChange={selectSection}
            tabBarGutter={24}
            tabBarStyle={{ margin: 0 }}
          />
        </div>

        <div className="flex-shrink-0 px-4 py-2.5 border-b border-gray-100 bg-gray-50/70">
          <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
            {activeSection.videos.map((video, index) => {
              const selected = index === activeVideoIndex;
              return (
                <button
                  key={`${activeSection.id}-${video.title}`}
                  type="button"
                  onClick={() => setActiveVideoIndex(index)}
                  className={`flex-shrink-0 h-9 px-3 rounded-lg border text-[11px] font-medium flex items-center gap-2 transition-colors ${
                    selected
                      ? "bg-[#4951EC] border-[#4951EC] text-white"
                      : "bg-white border-gray-200 text-gray-600 hover:border-[#9EA3FF] hover:text-[#4951EC]"
                  }`}
                  aria-pressed={selected}
                >
                  <span
                    className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] ${
                      selected ? "bg-white/20" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span>{video.title}</span>
                  <span className={selected ? "text-white/75" : "text-gray-400"}>
                    {video.duration}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.45fr)_minmax(300px,0.72fr)] gap-4 min-h-full">
            <section className="min-w-0 flex flex-col">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 text-[10.5px] font-medium text-[#4951EC]">
                    <span>{activeSection.title}</span>
                    <span className="text-gray-300">/</span>
                    <span>
                      Lesson {activeVideoIndex + 1} of {activeSection.videos.length}
                    </span>
                  </div>
                  <h2 className="text-[17px] font-semibold text-gray-900 leading-6">
                    {activeVideo.title}
                  </h2>
                </div>
                <span className="flex-shrink-0 h-7 px-2 rounded-lg bg-gray-100 text-gray-600 flex items-center gap-1.5 text-[11px]">
                  <Clock3 size={13} />
                  {activeVideo.duration}
                </span>
              </div>

              <div className="w-full bg-black rounded-lg overflow-hidden shadow-sm aspect-video">
                <iframe
                  key={activeVideo.url}
                  src={activeVideo.url}
                  title={`${activeSection.title} - ${activeVideo.title}`}
                  className="w-full h-full border-0"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>

              <div className="mt-3 flex items-start justify-between gap-4">
                <p className="text-[12px] leading-5 text-gray-600 max-w-2xl">
                  {activeVideo.description}
                </p>
                <a
                  href={activeVideo.url.replace("/preview", "/view")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 h-8 px-3 rounded-lg border border-gray-200 text-[11px] font-medium text-gray-600 hover:text-[#4951EC] hover:border-[#9EA3FF] flex items-center gap-1.5 transition-colors"
                >
                  <ExternalLink size={13} />
                  Open video
                </a>
              </div>
            </section>

            <aside className="min-w-0 rounded-lg border border-gray-200 bg-gray-50/60 p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <ListChecks size={17} className="text-[#4951EC]" />
                <h3 className="text-[13px] font-semibold text-gray-900">
                  Steps covered
                </h3>
              </div>
              <p className="text-[10.5px] text-gray-500 mb-3">
                Follow these steps while watching the video.
              </p>

              <ol className="space-y-2.5">
                {activeVideo.steps.map((step, index) => (
                  <li key={step} className="flex items-start gap-2.5">
                    <span className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-[#EEF0FF] text-[#4951EC] text-[10px] font-semibold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <span className="text-[11.5px] leading-[18px] text-gray-700">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>

              <div className="mt-auto pt-4 border-t border-gray-200 flex items-center gap-2 text-[10.5px] text-gray-500">
                <Smartphone size={14} className="text-[#4951EC]" />
                <span>
                  {activeSection.id === "duty-flow-mobile"
                    ? "This workflow is completed in the Ceviliyar mobile app."
                    : "Complete this workflow from the portal unless shown otherwise."}
                </span>
              </div>
            </aside>
          </div>
        </div>

        <footer className="flex-shrink-0 px-4 py-2.5 border-t border-gray-100 flex items-center justify-between gap-3 bg-white">
          <button
            type="button"
            onClick={() => moveLesson(-1)}
            disabled={globalLessonIndex === 0}
            className="h-8 px-3 rounded-lg border border-gray-200 text-[11px] font-medium text-gray-600 flex items-center gap-1.5 hover:border-[#9EA3FF] hover:text-[#4951EC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={14} />
            Previous
          </button>

          <div className="hidden sm:flex items-center gap-1.5" aria-label="Lesson progress">
            {flatLessons.map((lesson, index) => (
              <button
                key={`${lesson.sectionId}-${lesson.video.title}`}
                type="button"
                onClick={() => openLesson(lesson)}
                className={`h-1.5 rounded-full transition-all ${
                  index === globalLessonIndex
                    ? "w-5 bg-[#4951EC]"
                    : "w-1.5 bg-gray-200 hover:bg-[#9EA3FF]"
                }`}
                aria-label={`Open ${lesson.video.title}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => moveLesson(1)}
            disabled={globalLessonIndex === flatLessons.length - 1}
            className="h-8 px-3 rounded-lg bg-[#4951EC] hover:bg-[#3d3acc] text-white text-[11px] font-medium flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next video
            <ChevronRight size={14} />
          </button>
        </footer>
      </main>
    </div>
  );
};

export default HowItWorks;
